import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  DatePicker,
  Select,
  Button,
  Table,
  Tag,
  Input,
  Collapse,
  Space,
  Drawer,
  Form,
  Pagination,
  message,
  Spin,
  Modal,
  Tabs,
} from 'antd';
import {
  FileTextOutlined,
  UserOutlined,
  ShopOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { useQuery, useMutation } from 'react-query';
import api from '../config/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;

const Analytics: React.FC = () => {
  console.log('[Analytics] Component rendering...');
  const navigate = useNavigate();
  
  const [dateRange, setDateRange] = useState<any>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [businessTicketsModalVisible, setBusinessTicketsModalVisible] = useState(false);
  const [daysRange, setDaysRange] = useState<number>(7);
  const [quickDateFilter, setQuickDateFilter] = useState<string | null>(null);
  const [topBusinessesPage, setTopBusinessesPage] = useState<number>(1);
  const [topBusinessesPageSize] = useState<number>(10);
  const [selectedCustomFields, setSelectedCustomFields] = useState<string[]>([]);
  const [ticketsModalVisible, setTicketsModalVisible] = useState(false);
  const [selectedFieldForTickets, setSelectedFieldForTickets] = useState<{ fieldKey: string; fieldLabel: string; value: string } | null>(null);
  const [categoryRecurringOpen, setCategoryRecurringOpen] = useState(false);
  const [selectedCategoryForRecurring, setSelectedCategoryForRecurring] = useState<{ id: string; name: string } | null>(null);
  const [categoryTicketDrilldownOpen, setCategoryTicketDrilldownOpen] = useState(false);
  const [slaLongestPage, setSlaLongestPage] = useState(1);
  const [slaLongestPageSize] = useState(10);
  const [categoryTicketDrilldown, setCategoryTicketDrilldown] = useState<{
    business_id: string;
    business_name: string;
    issue_kind: string;
    issue_label: string;
  } | null>(null);

  const [reportMeasure, setReportMeasure] = useState<string>('ticket_count');
  const [reportGroupBy, setReportGroupBy] = useState<string>('week');
  const [reportCustomFieldKey, setReportCustomFieldKey] = useState<string>('');
  const [reportResult, setReportResult] = useState<{
    measure: string;
    groupBy: string;
    rows: { group_label: string; value: number }[];
  } | null>(null);

  // Handle quick date filter changes
  const handleQuickDateFilter = (days: number | null) => {
    if (days === null) {
      setQuickDateFilter(null);
      setDateRange(null);
      setDaysRange(7); // Reset to default
      return;
    }
    setQuickDateFilter(days.toString());
    setDaysRange(days); // Update daysRange to match the filter
    const endDate = dayjs();
    const startDate = endDate.subtract(days - 1, 'day');
    setDateRange([startDate, endDate]);
  };

  // Build params for API calls
  const getParams = () => {
    const params: any = {};
    if (dateRange && dateRange.length === 2) {
      params.from = dateRange[0].startOf('day').toISOString();
      params.to = dateRange[1].endOf('day').toISOString();
    }
    return params;
  };

  // Fetch summary stats (no filters - always shows all data)
  const { data: summaryData, isLoading: summaryLoading, error: summaryError } = useQuery(
    ['analytics-summary'],
    async () => {
      console.log('[Analytics] Fetching summary data...');
      try {
        const summaryRes = await api.get('/api/analytics/summary');
        
        return {
          totals: summaryRes.data.totals,
          statusCounts: summaryRes.data.statusCounts,
        };
      } catch (error) {
        console.error('[Analytics] Error fetching summary data:', error);
        throw error;
      }
    },
    {
      retry: false,
      onError: (error) => {
        console.error('[Analytics] Summary query error:', error);
        message.error('Failed to load summary data');
      }
    }
  );

  // Fetch chart data (with filters applied)
  const { data: chartData, isLoading: chartLoading } = useQuery(
    ['analytics-charts', dateRange?.[0]?.toISOString(), dateRange?.[1]?.toISOString(), daysRange], 
    async () => {
      console.log('[Analytics] Fetching chart data...');
      try {
        const params = getParams();
        const [byDayRes, slaRes, priorityRes, agentRes, typesRes, statusRes] = await Promise.all([
          api.get('/api/analytics/tickets-by-day', { params: { ...params, days: daysRange } }),
          api.get('/api/analytics/sla', { params }),
          api.get('/api/analytics/priority-distribution', { params }),
          api.get('/api/analytics/agent-performance', { params }),
          api.get('/api/analytics/ticket-categories', { params }),
          api.get('/api/analytics/summary', { params }),
        ]);
        
        console.log('[Analytics] Chart data loaded successfully');
        return {
          byDay: byDayRes.data.days,
          sla: slaRes.data,
          priorities: priorityRes.data.priorities,
          agents: agentRes.data.agents,
          ticketCategories: typesRes.data.categories,
          statusCounts: statusRes.data.statusCounts,
        };
      } catch (error) {
        console.error('[Analytics] Error fetching chart data:', error);
        throw error;
      }
    },
    {
      retry: false,
      onError: (error) => {
        console.error('[Analytics] Chart query error:', error);
        message.error('Failed to load chart data');
      }
    }
  );

  const { data: slaComplianceData, isLoading: slaComplianceLoading } = useQuery(
    ['analytics-sla-compliance', dateRange?.[0]?.toISOString(), dateRange?.[1]?.toISOString()],
    async () => {
      const res = await api.get('/api/analytics/sla-compliance', { params: getParams() });
      return res.data;
    },
    { retry: false }
  );

  const { data: slaLongestData, isLoading: slaLongestLoading } = useQuery(
    ['analytics-sla-longest', dateRange?.[0]?.toISOString(), dateRange?.[1]?.toISOString(), slaLongestPage],
    async () => {
      const res = await api.get('/api/analytics/sla-longest-resolve', {
        params: { ...getParams(), page: slaLongestPage, limit: slaLongestPageSize },
      });
      return res.data;
    },
    { retry: false }
  );

  // Fetch top 25 businesses
  const { data: topBusinessesData, isLoading: topBusinessesLoading } = useQuery(
    ['top-businesses-25'],
    async () => {
      const response = await api.get('/api/analytics/top-businesses', {
        params: { limit: 25 }
      });
      return response.data;
    },
    {
      retry: false,
      onError: (error) => {
        console.error('[Analytics] Top businesses query error:', error);
        message.error('Failed to load top businesses');
      }
    }
  );

  const { data: topIssuesData, isLoading: topIssuesLoading, refetch: refetchTopIssues } = useQuery(
    ['analytics-top-issues', dateRange?.[0]?.toISOString(), dateRange?.[1]?.toISOString()],
    async () => {
      const params = {
        ...getParams(),
        limit: 10,
      };
      const response = await api.get('/api/analytics/top-issues', { params });
      return response.data;
    },
    {
      retry: false,
      onError: (error) => {
        console.error('[Analytics] Top issues query error:', error);
        message.error('Failed to load top issues');
      }
    }
  );

  // Fetch tickets for selected custom field value
  const { data: filteredTicketsData, isLoading: filteredTicketsLoading } = useQuery(
    ['analytics-filtered-tickets', selectedFieldForTickets?.fieldKey, selectedFieldForTickets?.value, dateRange?.[0]?.toISOString(), dateRange?.[1]?.toISOString()],
    async () => {
      if (!selectedFieldForTickets) return null;
      const params = {
        custom_field_key: selectedFieldForTickets.fieldKey,
        custom_field_value: selectedFieldForTickets.value,
        ...getParams(),
        limit: 1000, // Get all matching tickets for export
      };
      const response = await api.get('/api/analytics/tickets-by-custom-field', { params });
      return response.data;
    },
    {
      enabled: !!selectedFieldForTickets,
      retry: false,
      onError: (error) => {
        console.error('[Analytics] Filtered tickets query error:', error);
        message.error('Failed to load tickets');
      }
    }
  );

  const { data: categoryRecurringData, isLoading: categoryRecurringLoading } = useQuery(
    [
      'category-recurring-issues',
      selectedCategoryForRecurring?.id,
      dateRange?.[0]?.toISOString(),
      dateRange?.[1]?.toISOString(),
    ],
    async () => {
      const params: Record<string, string> = {
        category_id: selectedCategoryForRecurring!.id,
        min_count: '2',
        limit: '80',
        ...getParams(),
      };
      return (await api.get('/api/analytics/category-recurring-issues', { params })).data;
    },
    {
      enabled: categoryRecurringOpen && !!selectedCategoryForRecurring?.id,
      retry: false,
      onError: () => message.error('Failed to load category recurring issues'),
    }
  );

  const { data: categoryRecurringTicketsData, isLoading: categoryRecurringTicketsLoading } = useQuery(
    [
      'category-recurring-tickets',
      selectedCategoryForRecurring?.id,
      categoryTicketDrilldown?.business_id,
      categoryTicketDrilldown?.issue_kind,
      categoryTicketDrilldown?.issue_label,
      dateRange?.[0]?.toISOString(),
      dateRange?.[1]?.toISOString(),
    ],
    async () => {
      const params: Record<string, string> = {
        category_id: selectedCategoryForRecurring!.id,
        business_id: categoryTicketDrilldown!.business_id,
        issue_kind: categoryTicketDrilldown!.issue_kind,
        issue_label: categoryTicketDrilldown!.issue_label,
        ...getParams(),
        limit: '500',
      };
      return (await api.get('/api/analytics/category-recurring-tickets', { params })).data;
    },
    {
      enabled:
        categoryTicketDrilldownOpen &&
        !!selectedCategoryForRecurring?.id &&
        !!categoryTicketDrilldown?.business_id,
      retry: false,
      onError: () => message.error('Failed to load tickets for this pattern'),
    }
  );

  const { data: reportFieldConfig } = useQuery(
    ['analytics-issue-signature-config'],
    () => api.get('/api/analytics/issue-signature-config').then((r) => r.data),
    { staleTime: 5 * 60 * 1000, retry: false }
  );

  const runReportMutation = useMutation(
    async () => {
      const filters: Record<string, string> = { ...getParams() };
      const body: Record<string, unknown> = {
        measure: reportMeasure,
        groupBy: reportGroupBy,
        filters,
      };
      if (reportGroupBy === 'custom_field') {
        body.custom_field_key = reportCustomFieldKey.trim();
      }
      const res = await api.post('/api/analytics/report-query', body);
      return res.data;
    },
    {
      onSuccess: (data) => {
        setReportResult(data);
        message.success('Report ready');
      },
      onError: (e: any) => {
        message.error(e?.response?.data?.error || 'Report failed');
      },
    }
  );

  const handleExportReport = () => {
    if (!reportResult?.rows?.length) {
      message.warning('Run a report with results first');
      return;
    }
    const worksheetData = reportResult.rows.map((r) => ({
      Group: r.group_label,
      Value: r.value,
    }));
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    const safe = `${reportResult.measure}_${reportResult.groupBy}`.replace(/[^a-zA-Z0-9_-]+/g, '_');
    XLSX.writeFile(workbook, `analytics_report_${safe}_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    message.success('Exported');
  };

  const handleCountClick = (fieldKey: string, fieldLabel: string, value: string) => {
    setSelectedFieldForTickets({ fieldKey, fieldLabel, value });
    setTicketsModalVisible(true);
  };

  const handleExportToExcel = () => {
    if (!filteredTicketsData?.tickets || filteredTicketsData.tickets.length === 0) {
      message.warning('No tickets to export');
      return;
    }

    const tickets = filteredTicketsData.tickets;
    const worksheetData = tickets.map((ticket: any) => ({
      'Ticket #': ticket.ticket_number,
      'Subject': ticket.subject,
      'Business': ticket.business_name || '',
      'Status': ticket.status,
      'Priority': ticket.priority,
      'Assignee': ticket.assignee_first_name && ticket.assignee_last_name 
        ? `${ticket.assignee_first_name} ${ticket.assignee_last_name}` 
        : 'Unassigned',
      'Category': ticket.category_name || '',
      'Created': dayjs(ticket.created_at).format('YYYY-MM-DD HH:mm'),
      'Updated': dayjs(ticket.updated_at).format('YYYY-MM-DD HH:mm'),
      'Resolved': ticket.resolved_at ? dayjs(ticket.resolved_at).format('YYYY-MM-DD HH:mm') : '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tickets');

    const fileName = `${selectedFieldForTickets?.fieldLabel || 'Tickets'}_${selectedFieldForTickets?.value || 'export'}_${dayjs().format('YYYY-MM-DD')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    message.success(`Exported ${tickets.length} tickets to ${fileName}`);
  };

  // Fetch tickets for selected business (must be before early returns)
  const { data: businessTicketsData, isLoading: businessTicketsLoading } = useQuery(
    ['business-tickets', selectedBusiness?.id],
    async () => {
      if (!selectedBusiness?.id) return null;
      const response = await api.get('/api/analytics/tickets', {
        params: { business_id: selectedBusiness.id, limit: 100 }
      });
      return response.data;
    },
    { enabled: !!selectedBusiness?.id }
  );

  // Get top issues fields data (before early returns for useEffect)
  const topIssuesFields = useMemo(() => topIssuesData?.fields ?? [], [topIssuesData]);

  const { data: reopenSummaryData, isLoading: reopenLoading } = useQuery(
    ['analytics-reopen-summary', dateRange?.[0]?.toISOString(), dateRange?.[1]?.toISOString()],
    async () => {
      const params = getParams();
      return (await api.get('/api/analytics/reopen-summary', { params })).data;
    },
    { retry: false }
  );

  // Auto-select all fields when data loads (must be before early returns)
  useEffect(() => {
    if (topIssuesFields.length > 0 && selectedCustomFields.length === 0) {
      setSelectedCustomFields(topIssuesFields.map((f: any) => f.field_key));
    }
  }, [topIssuesFields, selectedCustomFields.length]);

  // Filter fields based on selection
  const filteredFields = topIssuesFields.filter((field: any) => 
    selectedCustomFields.length === 0 || selectedCustomFields.includes(field.field_key)
  );

  // Show loading only for initial load (when both are loading)
  if (summaryLoading && chartLoading) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Loading analytics..." />
      </div>
    );
  }

  // Show error only if summary fails (charts can fail independently)
  if (summaryError) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Title level={4} type="danger">Failed to Load Analytics</Title>
          <Text>An error occurred while loading analytics data. Please try refreshing the page.</Text>
        </Card>
      </div>
    );
  }

  // Use real data or fallback to empty
  const summary = summaryData || {
    totals: { tickets: 0, businesses: 0, users: 0 },
    statusCounts: { new: 0, open: 0, pending: 0, solved: 0 },
  };

  const charts = chartData || {
    byDay: [],
    sla: { withinSLA: 0, outsideSLA: 0 },
    priorities: [],
    agents: [],
    ticketCategories: [],
    statusCounts: { new: 0, open: 0, pending: 0, solved: 0 },
  };

  const handleBusinessClick = (business: any) => {
    setSelectedBusiness(business);
    setBusinessTicketsModalVisible(true);
  };

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
      <Title level={2} style={{ marginBottom: 24 }}>Analytics & Reports</Title>
      
      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Tickets"
              value={summary.totals.tickets}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Businesses"
              value={summary.totals.businesses}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Users"
              value={summary.totals.users}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Solved"
              value={summary.statusCounts.solved}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Quick date range applies to charts and overview metrics */}
      <Card style={{ marginBottom: 24 }}>
        <Space size="middle" wrap>
          <Text strong>Quick Date Filters:</Text>
          <Button
            type={quickDateFilter === '7' ? 'primary' : 'default'}
            size="small"
            onClick={() => handleQuickDateFilter(7)}
          >
            7 Days
          </Button>
          <Button
            type={quickDateFilter === '30' ? 'primary' : 'default'}
            size="small"
            onClick={() => handleQuickDateFilter(30)}
          >
            30 Days
          </Button>
          <Button
            type={quickDateFilter === '90' ? 'primary' : 'default'}
            size="small"
            onClick={() => handleQuickDateFilter(90)}
          >
            90 Days
          </Button>
          <Button
            type={quickDateFilter === null && dateRange === null ? 'primary' : 'default'}
            size="small"
            onClick={() => handleQuickDateFilter(null)}
          >
            All Time
          </Button>
          <RangePicker
            value={dateRange}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setQuickDateFilter(null);
                setDateRange(dates);
                const daysDiff = dates[1].diff(dates[0], 'day') + 1;
                setDaysRange(Math.min(daysDiff, 90));
              } else {
                setDateRange(null);
                setDaysRange(7);
              }
            }}
            format="YYYY-MM-DD"
            placeholder={['Start Date', 'End Date']}
            allowClear
          />
          {dateRange && (
            <Button
              size="small"
              onClick={() => {
                setDateRange(null);
                setQuickDateFilter(null);
                setDaysRange(7);
              }}
            >
              Clear Date Filter
            </Button>
          )}
        </Space>
      </Card>

      <Tabs
        defaultActiveKey="overview"
        items={[
          {
            key: 'overview',
            label: 'Overview',
            children: (
              <>
      {/* Top 25 Businesses with Pagination */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ShopOutlined />
                <span>Top 25 Businesses - Most Tickets Generated</span>
              </div>
            }
          >
            {topBusinessesLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
              </div>
            ) : topBusinessesData?.businesses && topBusinessesData.businesses.length > 0 ? (
              <div>
                {(() => {
                  const startIndex = (topBusinessesPage - 1) * topBusinessesPageSize;
                  const endIndex = startIndex + topBusinessesPageSize;
                  const paginatedBusinesses = topBusinessesData.businesses.slice(startIndex, endIndex);
                  
                  return (
                    <>
                      {paginatedBusinesses.map((business: any, index: number) => {
                        const globalIndex = startIndex + index;
                        // Build address from available parts
                        const addressParts = [
                          business.address,
                          business.city,
                          business.state,
                          business.zip_code
                        ].filter((part: any) => part && String(part).trim() !== '');
                        const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : null;
                        
                        return (
                          <div
                            key={business.id || globalIndex}
                            onClick={() => handleBusinessClick(business)}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '16px',
                              marginBottom: index < paginatedBusinesses.length - 1 ? '12px' : 0,
                              background: globalIndex === 0 ? '#f0f9ff' : '#fafafa',
                              border: globalIndex === 0 ? '2px solid #1890ff' : '1px solid #e8e8e8',
                              borderRadius: '4px',
                              transition: 'all 0.3s',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = globalIndex === 0 ? '#e6f7ff' : '#f0f0f0';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = globalIndex === 0 ? '#f0f9ff' : '#fafafa';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    background: globalIndex === 0 ? '#1890ff' : globalIndex === 1 ? '#52c41a' : globalIndex === 2 ? '#faad14' : '#d9d9d9',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    fontSize: '13px',
                                  }}
                                >
                                  {globalIndex + 1}
                                </span>
                                <Text strong style={{ fontSize: '16px' }}>
                                  {business.business_name || 'Unknown Business'}
                                </Text>
                              </div>
                              <div style={{ marginLeft: '36px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {business.mid && (
                                  <Text type="secondary" style={{ fontSize: '13px' }}>
                                    <strong>MID:</strong> {business.mid}
                                  </Text>
                                )}
                                {fullAddress && (
                                  <Text type="secondary" style={{ fontSize: '13px' }}>
                                    <strong>Address:</strong> {fullAddress}
                                  </Text>
                                )}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                              <div style={{ textAlign: 'right' }}>
                                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                  Total Tickets
                                </Text>
                                <Text strong style={{ fontSize: '20px', color: '#1890ff' }}>
                                  {business.ticket_count || 0}
                                </Text>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                          current={topBusinessesPage}
                          total={topBusinessesData.businesses.length}
                          pageSize={topBusinessesPageSize}
                          showSizeChanger={false}
                          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} businesses`}
                          onChange={(page) => setTopBusinessesPage(page)}
                        />
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No business data available</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Top Issues (Custom Fields) */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileTextOutlined />
                <span>Top Issues - Custom Fields</span>
              </div>
            }
            extra={
              <Space>
                {topIssuesFields.length > 0 && (
                  <Select
                    mode="multiple"
                    placeholder="Select custom fields to display"
                    value={selectedCustomFields}
                    onChange={setSelectedCustomFields}
                    style={{ minWidth: 300 }}
                    maxTagCount="responsive"
                    allowClear
                  >
                    {topIssuesFields.map((field: any) => (
                      <Select.Option key={field.field_key} value={field.field_key}>
                        {field.label} ({field.usage_count} tickets)
                      </Select.Option>
                    ))}
                  </Select>
                )}
                <Button size="small" icon={<ReloadOutlined />} onClick={() => refetchTopIssues()}>
                  Refresh
                </Button>
              </Space>
            }
          >
            {topIssuesLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
              </div>
            ) : !topIssuesFields || topIssuesFields.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No custom fields found. Add ticket custom fields to analyze top issues.</Text>
              </div>
            ) : filteredFields.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No custom fields selected. Please select fields from the dropdown above.</Text>
              </div>
            ) : (
              <div>
                {filteredFields.map((field: any, fieldIndex: number) => (
                  <div key={field.field_key} style={{ marginBottom: fieldIndex < filteredFields.length - 1 ? 32 : 0 }}>
                    <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                      <Text strong style={{ fontSize: '16px' }}>
                        {field.label}
                      </Text>
                      <Text type="secondary" style={{ marginLeft: 8, fontSize: '13px' }}>
                        ({field.usage_count} tickets)
                      </Text>
                    </div>
                    {field.issues && field.issues.length > 0 ? (
                      <Table
                        dataSource={field.issues.map((issue: any, index: number) => ({
                          key: `${field.field_key}-${issue.value}-${index}`,
                          issue: issue.value || 'Unknown',
                          count: issue.count || 0,
                        }))}
                        columns={[
                          {
                            title: 'Issue',
                            dataIndex: 'issue',
                            key: 'issue',
                            render: (text: string, record: any, index: number) => (
                              <Space>
                                <Tag color={index === 0 ? 'blue' : index === 1 ? 'green' : 'geekblue'}>{index + 1}</Tag>
                                <span>{text}</span>
                              </Space>
                            ),
                          },
                          {
                            title: 'Ticket Count',
                            dataIndex: 'count',
                            key: 'count',
                            width: 150,
                            render: (count: number, record: any) => (
                              <Tag 
                                color="purple" 
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleCountClick(field.field_key, field.label, record.issue)}
                              >
                                {count}
                              </Tag>
                            ),
                          },
                        ]}
                        pagination={false}
                        size="small"
                        style={{ marginBottom: 0 }}
                      />
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                        <Text type="secondary">No issues found for this field.</Text>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card 
            title="Ticket reopens (audit trail)"
            extra={reopenLoading ? <Spin size="small" /> : null}
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              {reopenSummaryData?.description || 'Status changes from solved back to open/pending/new, from ticket audit logs.'}
            </Text>
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Total reopens in range"
                  value={reopenSummaryData?.totalReopens ?? 0}
                />
              </Col>
              <Col xs={24} sm={16}>
                {reopenLoading ? (
                  <Spin />
                ) : (
                  <Table
                    size="small"
                    dataSource={(reopenSummaryData?.byCategory || []).map((r: any, i: number) => ({ ...r, key: i }))}
                    columns={[
                      { title: 'Category', dataIndex: 'category_name', key: 'category_name' },
                      { title: 'Reopens', dataIndex: 'reopen_count', key: 'reopen_count', width: 100 },
                    ]}
                    pagination={false}
                  />
                )}
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Charts Row 1 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Card 
            title="Weekly Ticket Trend" 
            style={{ marginBottom: 16 }}
            extra={chartLoading ? <Spin size="small" /> : null}
          >
            {chartLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Spin tip="Loading chart data..." />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart 
                  data={(charts.byDay || []).map((d: any) => ({
                    day: dayjs(d.day).format('MMM D'),
                    created: d.created || 0,
                    solved: d.solved || 0,
                  }))}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="day" 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  <YAxis 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e8e8e8',
                      borderRadius: '4px'
                    }}
                    labelStyle={{ fontWeight: 'bold' }}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="created" 
                    stroke="#1890ff" 
                    strokeWidth={2}
                    name="Created" 
                    dot={{ fill: '#1890ff', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="solved" 
                    stroke="#52c41a" 
                    strokeWidth={2}
                    name="Solved" 
                    dot={{ fill: '#52c41a', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>

      {/* Charts Row 2 - Priority Distribution & Agent Performance */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card 
            title="Priority Distribution" 
            style={{ marginBottom: 16 }}
            extra={chartLoading ? <Spin size="small" /> : null}
          >
            {chartLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Spin tip="Loading chart data..." />
              </div>
            ) : (charts.priorities && charts.priorities.length > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={(charts.priorities || []).map((p: any) => ({
                    name: p.priority ? p.priority.charAt(0).toUpperCase() + p.priority.slice(1) : 'Unknown',
                    value: p.count || 0,
                    color: p.priority === 'high' ? '#fa8c16' : 
                           p.priority === 'medium' ? '#faad14' : '#52c41a'
                  }))}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" stroke="#666" tick={{ fill: '#666', fontSize: 12 }} />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                    width={80}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e8e8e8',
                      borderRadius: '4px'
                    }}
                    formatter={(value: any) => [value, 'Tickets']}
                  />
                  <Bar 
                  dataKey="value" 
                  radius={[0, 8, 8, 0]}
                >
                  {(charts.priorities || []).map((p: any, index: number) => {
                      const color = 
                                   p.priority === 'high' ? '#fa8c16' : 
                                   p.priority === 'medium' ? '#faad14' : '#52c41a';
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No priority data available</Text>
              </div>
            )}
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card 
            title="Top Agents by Ticket Volume" 
            style={{ marginBottom: 16 }}
            extra={chartLoading ? <Spin size="small" /> : null}
          >
            {chartLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Spin tip="Loading chart data..." />
              </div>
            ) : (charts.agents && charts.agents.length > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={(charts.agents || []).slice(0, 5).map((a: any) => ({
                    name: `${a.first_name || ''} ${a.last_name || ''}`.trim() || 'Unknown Agent',
                    total: a.total_tickets || 0,
                    solved: a.solved_tickets || 0,
                  }))}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 11 }}
                  />
                  <YAxis 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e8e8e8',
                      borderRadius: '4px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="total" fill="#1890ff" name="Total Tickets" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="solved" fill="#52c41a" name="Solved" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No agent data available</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Charts Row 3 - Ticket Types & SLA Metrics */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card 
            title="Tickets by Category" 
            style={{ marginBottom: 16 }}
            extra={
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {chartLoading ? <Spin size="small" /> : null}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Click a bar for recurring issues
                </Text>
              </div>
            }
          >
            {chartLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Spin tip="Loading chart data..." />
              </div>
            ) : (charts.ticketCategories && charts.ticketCategories.length > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={(charts.ticketCategories || []).map((c: any) => ({
                    name: c.category_name || 'Unassigned',
                    value: c.count || 0,
                    category_id: c.category_id,
                  }))}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e8e8e8',
                      borderRadius: '4px'
                    }}
                    formatter={(value: any) => [value, 'Tickets']}
                  />
                  <Bar 
                    dataKey="value" 
                    fill="#722ed1"
                    radius={[8, 8, 0, 0]}
                    cursor="pointer"
                    onClick={(barData: any) => {
                      const pl = barData?.payload ?? barData;
                      const id = pl?.category_id;
                      if (!id) {
                        message.info('Assign a product category on tickets to drill down; "Unassigned" has no category id.');
                        return;
                      }
                      setSelectedCategoryForRecurring({ id: String(id), name: pl?.name || 'Category' });
                      setCategoryRecurringOpen(true);
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No category data available</Text>
              </div>
            )}
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card 
            title="SLA Performance" 
            style={{ marginBottom: 16 }}
            extra={chartLoading ? <Spin size="small" /> : null}
          >
            {chartLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Spin tip="Loading chart data..." />
              </div>
            ) : charts.sla ? (
              <div style={{ padding: '20px 0' }}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title="Avg Resolution Time"
                      value={charts.sla.resolve?.avg_minutes || 0}
                      suffix="min"
                      valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Avg First Response"
                      value={charts.sla.first_response?.avg_minutes || 0}
                      suffix="min"
                      valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Median Resolution"
                      value={charts.sla.resolve?.p50_minutes || 0}
                      suffix="min"
                      valueStyle={{ color: '#faad14', fontSize: '20px' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="90th Percentile"
                      value={charts.sla.resolve?.p90_minutes || 0}
                      suffix="min"
                      valueStyle={{ color: '#f5222d', fontSize: '20px' }}
                    />
                  </Col>
                </Row>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No SLA data available</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="SLA Compliance" loading={slaComplianceLoading} style={{ marginBottom: 16 }}>
            {slaComplianceData?.summary ? (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col span={8}>
                    <Statistic title="Resolved" value={slaComplianceData.summary.total_resolved || 0} />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Met SLA"
                      value={slaComplianceData.summary.met_sla_pct || 0}
                      suffix="%"
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Breached"
                      value={slaComplianceData.summary.breached_sla_pct || 0}
                      suffix="%"
                      valueStyle={{ color: '#f5222d' }}
                    />
                  </Col>
                </Row>
                {(slaComplianceData.byPriority || []).length > 0 && (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={slaComplianceData.byPriority.map((p: any) => ({
                      name: p.priority,
                      met: p.met_sla,
                      breached: p.breached_sla,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="met" name="Met SLA" fill="#52c41a" stackId="a" />
                      <Bar dataKey="breached" name="Breached" fill="#f5222d" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </>
            ) : (
              <Text type="secondary">No SLA compliance data for the selected range</Text>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title="Longest to Resolve"
            extra={
              slaLongestData?.tickets?.length ? (
                <Button
                  size="small"
                  icon={<FileExcelOutlined />}
                  onClick={() => {
                    const rows = (slaLongestData.tickets || []).map((t: any) => ({
                      Ticket: t.ticket_number,
                      Subject: t.subject,
                      Business: t.business_name,
                      Category: t.category_name,
                      Priority: t.priority,
                      'Resolution (min)': t.resolution_minutes,
                      'SLA Target (min)': t.sla_target_minutes,
                      'Met SLA': t.met_sla ? 'Yes' : 'No',
                      Assignee: t.assignee_name,
                    }));
                    const ws = XLSX.utils.json_to_sheet(rows);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'Longest Resolve');
                    XLSX.writeFile(wb, 'sla-longest-resolve.xlsx');
                  }}
                >
                  Export
                </Button>
              ) : null
            }
            style={{ marginBottom: 16 }}
          >
            <Table
              loading={slaLongestLoading}
              size="small"
              rowKey="id"
              dataSource={slaLongestData?.tickets || []}
              pagination={{
                current: slaLongestPage,
                pageSize: slaLongestPageSize,
                total: slaLongestData?.pagination?.total || 0,
                onChange: (p) => setSlaLongestPage(p),
                showSizeChanger: false,
              }}
              columns={[
                { title: 'Ticket', dataIndex: 'ticket_number', width: 90 },
                { title: 'Subject', dataIndex: 'subject', ellipsis: true },
                { title: 'Priority', dataIndex: 'priority', width: 80, render: (p: string) => <Tag>{p}</Tag> },
                {
                  title: 'Duration',
                  dataIndex: 'resolution_minutes',
                  width: 90,
                  render: (m: number) => `${m} min`,
                },
                {
                  title: 'SLA',
                  dataIndex: 'met_sla',
                  width: 80,
                  render: (met: boolean) => (
                    <Tag color={met ? 'green' : 'red'}>{met ? 'Met' : 'Breached'}</Tag>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Report builder (beta)" style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Choose a metric (measure) and how to slice it (group by). Uses the same date range as charts when set.
              Inspired by metrics and attributes in tools like Zendesk Explore—limited to a safe allowlist on the server.
            </Text>
            <Space wrap style={{ marginBottom: 16 }}>
              <Select
                style={{ minWidth: 200 }}
                value={reportMeasure}
                onChange={setReportMeasure}
                options={[
                  { value: 'ticket_count', label: 'Ticket count' },
                  { value: 'solved_count', label: 'Solved count' },
                  { value: 'avg_resolve_hours', label: 'Avg. resolve time (hours)' },
                ]}
              />
              <Select
                style={{ minWidth: 200 }}
                value={reportGroupBy}
                onChange={(v) => {
                  setReportGroupBy(v);
                  if (v !== 'custom_field') setReportCustomFieldKey('');
                }}
                options={[
                  { value: 'day', label: 'Day (created)' },
                  { value: 'week', label: 'Week (created)' },
                  { value: 'month', label: 'Month (created)' },
                  { value: 'category', label: 'Category' },
                  { value: 'status', label: 'Status' },
                  { value: 'priority', label: 'Priority' },
                  { value: 'business', label: 'Business' },
                  { value: 'assignee', label: 'Assignee' },
                  { value: 'custom_field', label: 'Custom field value' },
                ]}
              />
              {reportGroupBy === 'custom_field' ? (
                <Select
                  showSearch
                  allowClear
                  placeholder="Custom field key"
                  style={{ minWidth: 220 }}
                  value={reportCustomFieldKey || undefined}
                  onChange={(v) => setReportCustomFieldKey(v || '')}
                  options={(reportFieldConfig?.fields || []).map((f: { field_key: string; label: string }) => ({
                    value: f.field_key,
                    label: `${f.label} (${f.field_key})`,
                  }))}
                />
              ) : null}
              <Button
                type="primary"
                loading={runReportMutation.isLoading}
                onClick={() => {
                  if (reportGroupBy === 'custom_field' && !reportCustomFieldKey.trim()) {
                    message.error('Select a custom field');
                    return;
                  }
                  runReportMutation.mutate();
                }}
              >
                Run report
              </Button>
              <Button icon={<FileExcelOutlined />} onClick={handleExportReport} disabled={!reportResult?.rows?.length}>
                Export Excel
              </Button>
            </Space>
            {reportResult?.rows?.length ? (
              <Table
                size="small"
                rowKey={(r) => `${r.group_label}-${r.value}`}
                dataSource={reportResult.rows}
                pagination={{ pageSize: 20, showTotal: (t) => `${t} rows` }}
                columns={[
                  {
                    title: 'Group',
                    dataIndex: 'group_label',
                    key: 'group_label',
                    ellipsis: true,
                  },
                  {
                    title: reportResult.measure === 'avg_resolve_hours' ? 'Hours' : 'Count',
                    dataIndex: 'value',
                    key: 'value',
                    width: 120,
                  },
                ]}
              />
            ) : (
              <Text type="secondary">Run a report to see results here (up to 500 groups).</Text>
            )}
          </Card>
        </Col>
      </Row>
              </>
            ),
          },
        ]}
      />

      {/* Business Tickets Modal */}
      <Modal
        title={
          selectedBusiness ? (
            <div>
              <Text strong style={{ fontSize: '18px' }}>Tickets for {selectedBusiness.business_name}</Text>
              {selectedBusiness.mid && (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: '13px' }}>MID: {selectedBusiness.mid}</Text>
                </div>
              )}
            </div>
          ) : 'Business Tickets'
        }
        open={businessTicketsModalVisible}
        onCancel={() => {
          setBusinessTicketsModalVisible(false);
          setSelectedBusiness(null);
        }}
        footer={null}
        width={1200}
      >
        {businessTicketsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : businessTicketsData?.tickets && businessTicketsData.tickets.length > 0 ? (
          <Table
            dataSource={businessTicketsData.tickets}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} tickets`,
            }}
            columns={[
              {
                title: 'Ticket #',
                dataIndex: 'ticket_number',
                key: 'ticket_number',
                width: 120,
                render: (text: string, record: any) => (
                  <Text 
                    code 
                    style={{ cursor: 'pointer', color: '#1890ff' }}
                    onClick={() => navigate(`/workspace/number/${record.ticket_number}`)}
                  >
                    {text}
                  </Text>
                ),
              },
              {
                title: 'Subject',
                dataIndex: 'subject',
                key: 'subject',
                ellipsis: true,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (status: string) => {
                  const colors: any = { new: 'blue', open: 'orange', pending: 'gold', solved: 'green' };
                  return <Tag color={colors[status]}>{status?.toUpperCase()}</Tag>;
                },
              },
              {
                title: 'Priority',
                dataIndex: 'priority',
                key: 'priority',
                width: 100,
                render: (priority: string) => {
                  const colors: any = { low: 'default', medium: 'blue', high: 'orange' };
                  return <Tag color={colors[priority]}>{priority?.toUpperCase()}</Tag>;
                },
              },
              {
                title: 'Assignee',
                key: 'assignee',
                width: 150,
                render: (record: any) => 
                  record.assignee_first_name ? `${record.assignee_first_name} ${record.assignee_last_name}` : 'Unassigned',
              },
              {
                title: 'Created',
                dataIndex: 'created_at',
                key: 'created_at',
                width: 150,
                render: (date: string) => dayjs(date).format('MMM D, YYYY HH:mm'),
              },
              {
                title: 'Closed',
                dataIndex: 'resolved_at',
                key: 'resolved_at',
                width: 150,
                render: (date: string | null) => {
                  if (!date) {
                    return <Text type="secondary">-</Text>;
                  }
                  return dayjs(date).format('MMM D, YYYY HH:mm');
                },
              },
            ]}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">No tickets found for this business</Text>
          </div>
        )}
      </Modal>

      <Modal
        title={
          selectedCategoryForRecurring ? (
            <div>
              <Text strong style={{ fontSize: '18px' }}>
                Recurring issues: {selectedCategoryForRecurring.name}
              </Text>
              {categoryRecurringData?.signatureField ? (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Signature field: {categoryRecurringData.signatureField.label} — rows without this value are grouped by
                    normalized subject.
                  </Text>
                </div>
              ) : (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    No custom signature field; grouping by normalized subject only.
                  </Text>
                </div>
              )}
            </div>
          ) : (
            'Category recurring issues'
          )
        }
        open={categoryRecurringOpen}
        onCancel={() => {
          setCategoryRecurringOpen(false);
          setSelectedCategoryForRecurring(null);
          setCategoryTicketDrilldown(null);
          setCategoryTicketDrilldownOpen(false);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setCategoryRecurringOpen(false);
              setSelectedCategoryForRecurring(null);
              setCategoryTicketDrilldown(null);
              setCategoryTicketDrilldownOpen(false);
            }}
          >
            Close
          </Button>,
        ]}
        width={1000}
      >
        {categoryRecurringLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            dataSource={categoryRecurringData?.rows || []}
            rowKey={(r: any) => `${r.business_id}-${r.issue_kind}-${r.issue_label}`}
            pagination={{ pageSize: 15, showTotal: (t) => `${t} patterns` }}
            locale={{ emptyText: 'No recurring patterns for this category in the selected date range (min. 2 tickets).' }}
            columns={[
              {
                title: 'Business',
                dataIndex: 'business_name',
                key: 'business_name',
                ellipsis: true,
              },
              {
                title: 'Type',
                dataIndex: 'issue_kind',
                key: 'issue_kind',
                width: 130,
                render: (k: string) => (
                  <Tag color={k === 'signature' ? 'blue' : 'purple'}>{k === 'signature' ? 'Field' : 'Subject'}</Tag>
                ),
              },
              {
                title: 'Issue',
                dataIndex: 'issue_label',
                key: 'issue_label',
                ellipsis: true,
                render: (text: string) => <Text style={{ fontSize: 13 }}>{text}</Text>,
              },
              {
                title: 'Tickets',
                dataIndex: 'ticket_count',
                key: 'ticket_count',
                width: 90,
              },
              {
                title: 'Open',
                dataIndex: 'open_count',
                key: 'open_count',
                width: 80,
              },
              {
                title: '',
                key: 'actions',
                width: 100,
                render: (_: unknown, record: any) => (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      setCategoryTicketDrilldown({
                        business_id: record.business_id,
                        business_name: record.business_name,
                        issue_kind: record.issue_kind,
                        issue_label: record.issue_label,
                      });
                      setCategoryTicketDrilldownOpen(true);
                    }}
                  >
                    Tickets
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Modal>

      <Modal
        title={
          categoryTicketDrilldown && selectedCategoryForRecurring ? (
            <div>
              <Text strong>Tickets</Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {categoryTicketDrilldown.business_name} — {categoryTicketDrilldown.issue_kind}:{' '}
                  {categoryTicketDrilldown.issue_label}
                </Text>
              </div>
            </div>
          ) : (
            'Tickets'
          )
        }
        open={categoryTicketDrilldownOpen}
        onCancel={() => {
          setCategoryTicketDrilldownOpen(false);
          setCategoryTicketDrilldown(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setCategoryTicketDrilldownOpen(false);
              setCategoryTicketDrilldown(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={1000}
      >
        {categoryRecurringTicketsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            dataSource={categoryRecurringTicketsData?.tickets || []}
            rowKey="id"
            pagination={{ pageSize: 15, showTotal: (t) => `${t} tickets` }}
            locale={{ emptyText: 'No tickets found.' }}
            columns={[
              {
                title: 'Ticket #',
                dataIndex: 'ticket_number',
                key: 'ticket_number',
                width: 120,
                render: (text: string, record: any) => (
                  <Text
                    code
                    style={{ cursor: 'pointer', color: '#1890ff' }}
                    onClick={() => navigate(`/workspace/number/${record.ticket_number}`)}
                  >
                    {text}
                  </Text>
                ),
              },
              { title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (status: string) => {
                  const colors: any = { new: 'blue', open: 'orange', pending: 'gold', solved: 'green' };
                  return <Tag color={colors[status]}>{status?.toUpperCase()}</Tag>;
                },
              },
              {
                title: 'Created',
                dataIndex: 'created_at',
                key: 'created_at',
                width: 160,
                render: (d: string) => dayjs(d).format('YYYY-MM-DD HH:mm'),
              },
            ]}
          />
        )}
      </Modal>

      {/* Filtered Tickets Modal */}
      <Modal
        title={
          selectedFieldForTickets ? (
            <div>
              <Text strong style={{ fontSize: '18px' }}>
                Tickets: {selectedFieldForTickets.fieldLabel} = "{selectedFieldForTickets.value}"
              </Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  {filteredTicketsData?.total || 0} ticket(s) found
                </Text>
              </div>
            </div>
          ) : 'Filtered Tickets'
        }
        open={ticketsModalVisible}
        onCancel={() => {
          setTicketsModalVisible(false);
          setSelectedFieldForTickets(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setTicketsModalVisible(false);
            setSelectedFieldForTickets(null);
          }}>
            Close
          </Button>,
          <Button 
            key="export" 
            type="primary" 
            icon={<FileExcelOutlined />}
            onClick={handleExportToExcel}
            disabled={!filteredTicketsData?.tickets || filteredTicketsData.tickets.length === 0}
          >
            Export to Excel
          </Button>,
        ]}
        width={1200}
      >
        {filteredTicketsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : filteredTicketsData?.tickets && filteredTicketsData.tickets.length > 0 ? (
          <Table
            dataSource={filteredTicketsData.tickets}
            rowKey="id"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} tickets`,
            }}
            columns={[
              {
                title: 'Ticket #',
                dataIndex: 'ticket_number',
                key: 'ticket_number',
                width: 120,
                render: (text: string, record: any) => (
                  <Text 
                    code 
                    style={{ cursor: 'pointer', color: '#1890ff' }}
                    onClick={() => navigate(`/workspace/number/${record.ticket_number}`)}
                  >
                    {text}
                  </Text>
                ),
              },
              {
                title: 'Subject',
                dataIndex: 'subject',
                key: 'subject',
                ellipsis: true,
              },
              {
                title: 'Business',
                dataIndex: 'business_name',
                key: 'business_name',
                ellipsis: true,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (status: string) => {
                  const colors: any = { new: 'blue', open: 'orange', pending: 'gold', solved: 'green' };
                  return <Tag color={colors[status]}>{status?.toUpperCase()}</Tag>;
                },
              },
              {
                title: 'Priority',
                dataIndex: 'priority',
                key: 'priority',
                width: 100,
                render: (priority: string) => {
                  const colors: any = { low: 'default', medium: 'blue', high: 'orange' };
                  return <Tag color={colors[priority]}>{priority?.toUpperCase()}</Tag>;
                },
              },
              {
                title: 'Assignee',
                key: 'assignee',
                width: 150,
                render: (record: any) => 
                  record.assignee_first_name ? `${record.assignee_first_name} ${record.assignee_last_name}` : 'Unassigned',
              },
              {
                title: 'Created',
                dataIndex: 'created_at',
                key: 'created_at',
                width: 150,
                render: (date: string) => dayjs(date).format('MMM D, YYYY HH:mm'),
              },
              {
                title: 'Closed',
                dataIndex: 'resolved_at',
                key: 'resolved_at',
                width: 150,
                render: (date: string | null) => {
                  if (!date) {
                    return <Text type="secondary">-</Text>;
                  }
                  return dayjs(date).format('MMM D, YYYY HH:mm');
                },
              },
            ]}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">No tickets found for this filter</Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Analytics;
